export interface FaqForm {
  title: string;
  email: string;
  content: string;
  rules: boolean;
}

export interface ReviewForm {
  points: number;
  name: string;
  email: string;
  content: string;
  images: Array<File>;
  rules: boolean;
}

export interface ProductReviewImage {
  id: string;
  url: string;
  title: string;
  metaData: { width: number; height: number; };
  alt: string;
}

export type ProductReviewImages = Array<ProductReviewImage>;

export interface ProductReview {
  id: string;
  productId: string;
  title: string;
  points: number;
  comment: string;
  content: string;
  createdAt: string;
  extensions: { 
    customerInfo: { name: string }; 
    bluePapricaProductReviewMediaItem?: ProductReviewImages;
  };
}

export type ProductReviews = Array<ProductReview>;

export interface ProductQuestionAnswer {
  id: string;
  createdAt: string;
  text: string;
}

export type ProductQuestionAnswers = Array<ProductQuestionAnswer>;

export interface ProductQuestion {
  id: string;
  title: string;
  text: string;
  createdAt: string;
  answers: ProductQuestionAnswers;
}

export type ProductQuestions = Array<ProductQuestion>;

export interface ProductManufacturer {
  id: string;
  prefix: string;
  seoUrl: string;
  name: string;
  pimcoreId: string;
  displayOnProductPage: boolean;
}

export type ProductManufacturers = Array<ProductManufacturer>;

export interface Product {
  id: string;
  [key: string]: any;
}

const getLocations = async (id: string, baseId: string, url: string) => {
  if (!baseId) {
    return []
  }

  const { elements: locations = [] } = await $fetch('/store-api/product', {
    method: 'POST',
    body: {
      associations: {
        seoUrls: {}
      },
      filter: [
        { type: 'equals', field: 'categoryIds', value: baseId },
        { type: 'equals', field: 'parentId', value: null },
        { // do not response products without seoUrls
          type: 'not',
          queries: [
            { field: 'seoUrls.id', type: 'equals', value: null }
          ]
        },
        {
          type: 'not',
          queries: [
            { field: 'id', type: 'equals', value: id }
          ]
        }
      ],
      includes: {
        product: [
          'id',
          'name',
          'translated',
          'seoUrls',
          'breadcrumbWithUrl',
          'urlPrefix',
          'customFields',
          'calculatedPrice',
          'calculatedCheapestPrice'
        ],
        calculated_price: [
          'unitPrice'
        ],
        seo_url: [
          'seoPathInfo'
        ]
      }
    },
    query: {
      url
    }
  })

  return locations
}

const getCombinations = async (id: string, parent: string | boolean | null, url: string) => {
  const { elements: availableOptions = [] } = await $fetch('/store-api/product', {
    method: 'POST',
    body: {
      filter: [
        { type: 'equals', field: Boolean(parent) ? 'parentId' : 'id', value: id },
        { type: 'equals', field: 'active', value: true }
      ],
      associations: {
        properties: {
          associations: {
            group: {}
          }
        },
        options: {
          associations: {
            group: {
              associations: {
                options: {}
              }
            }
          }
        }
      },
      includes: {
        product: [
          'id',
          'calculatedPrice',
          'calculatedPrices',
          'customFields',
          'description',
          'productNumber',
          'options',
          'optionIds',
          'seoUrls',
          'parentSeoUrl',
          'urlPrefix'
        ],
        calculated_price: [
          'quantity',
          'listPrice',
          'unitPrice'
        ],
        seo_url: [
          'seoPathInfo'
        ]
      }
    },
    query: {
      url
    }
  })

  return availableOptions
}

const getManufacturers = async (id: string | number, url: string) => {
  const { manufacturers = []} = await $fetch<{ manufacturers?: ProductManufacturers }>(`/store-api/manufacturer/by-product/${id}`, {
    method: 'GET',
    query: {
      url
    }
  })

  return manufacturers
}

const getReviewsPerPage = async (id: string, page: number, limit: number, url: string) => {
  const { elements, total } = await $fetch<{ elements: ProductReviews, total: number }>(`/store-api/product/${id}/reviews`, {
    method: 'POST',
    body: {
      page,
      limit,
      sort: [{ field: 'createdAt', order: 'desc' }],
      'total-count-mode': 1
    },
    query: {
      url
    }
  })

  return {
    elements,
    total,
    page,
    limit
  }
}

const getFaqPerPage = async (id: string, page: number, limit: number, url: string) => {
  const { data: elements, total } = await $fetch<{ data: ProductQuestions, total: number }>(`/store-api/cogi/qaa/questions/public/${id}`, {
    method: 'GET',
    params: {
      page,
      limit
    },
    query: {
      url
    }
  })

  return {
    elements,
    total,
    page,
    limit
  }
}

export const pageLimit = 3

export class RedirectError extends Error {
  constructor(redirect: string, opts?: { cause: unknown }) {
    super(redirect, opts)

    this.name = 'RedirectError'

    if (opts?.cause && !this.cause) {
      this.cause = opts.cause
    }
  }
}

interface ProductResponse {
  product: Product;
  configurator: any;
}

interface ProductResponseWithRedirect extends ProductResponse {
  redirect: true;
  redirectSeoUrl: string;
}

export const useProductsStore = defineStore('products', {
  state: () => {
    return {
      products: {} as Record<Product['id'], Product>,
    }
  },
  actions: {
    async init (slug: string, url: string): Promise<string> {
      const { product, configurator: combinations, ...response } = await $fetch<ProductResponse | ProductResponseWithRedirect>(`/store-api/url/${slug}`, {
        method: 'POST',
        body: {
          associations: {
            media: {
              sort: [
                { field: 'position', order: 'ASC', naturalSorting: false }
              ]
            },
            properties: {
              associations: {
                group: {}
              }
            },
            options: {
              associations: {
                group: {
                  associations: {
                    options: {}
                  }
                }
              }
            }
          },
          includes: {
            product: [
              'id',
              'name',
              'parentId',
              'metaTitle',
              'metaDescription',
              'productNumber',
              'active',
              'available',
              'seoUrls',
              'description',
              'calculatedPrice',
              'calculatedPrices',
              'calculatedCheapestPrice',
              'cmsPage',
              'properties',
              'manufacturer',
              'ratingAverage',
              'cover',
              'media',
              'options',
              'optionIds',
              'translated',
              'customFields',
              'breadcrumbWithUrl',
              'urlPrefix',
              'seoCategory',
              'updatedAt'
            ],
            calculated_price: [
              'quantity',
              'listPrice',
              'unitPrice'
            ],
            product_media: [
              'id',
              'media'
            ],
            media: [
              'id',
              'thumbnails',
              'metaData',
              'mimeType',
              'alt',
              'title',
              'url',
              'translated'
            ],
            media_thumbnail: [
              'id',
              'width',
              'height',
              'url'
            ],
            property_group_option: [
              'id',
              'groupId',
              'group',
              'name',
              'translated'
            ],
            seo_url: [
              'seoPathInfo'
            ],
            property_group: [
              'name',
              'id',
              'options',
              'translated'
            ]
          }
        },
        query: {
          url
        }
      })

      if ('redirect' in response && response.redirect) {
        throw new RedirectError(response.redirectSeoUrl)
      }

      const { 
        id, 
        parentId: parent, 
        customFields: { product_baseCategory: baseCategoryId, product_pimcoreProductId: pimcoreProductId }
      } = product

      const mainId = parent || id

      const [availableOptions, reviews, faq, locations, manufacturers] = await Promise.all([
        getCombinations(mainId, parent, url),
        getReviewsPerPage(mainId, 1, pageLimit, url),
        getFaqPerPage(mainId, 1, pageLimit, url),
        getLocations(mainId, baseCategoryId, url),
        getManufacturers(pimcoreProductId, url)
      ])

      this.$patch((state) => state.products[mainId] = {
        ...product,
        availableOptions,
        reviews: {
          ...reviews,
          isFetching: false
        },
        faq: { 
          ...faq,
          isFetching: false
        },
        locations,
        manufacturers,
        combinations
      })

      return mainId
    },
    async addReview ({ id, form }: { id: string, form: FormData }) {
      await $fetch(`/store-api/product-rating/${id}`, {
        method: 'POST',
        body: form
      })
    },
    async addQuestion ({ id, form }: { id: string, form: FaqForm }) {
      await $fetch('/store-api/question/new', {
        method: 'POST',
        body: {
          productId: id,
          ...form
        }
      })
    },
    async getMoreReviews (id: string, url: string) {
      const product = this.products[id]
      if (product && !product.reviews.isFetching) {
        product.reviews.isFetching = true
        const { elements, total, page } = await getReviewsPerPage(id, product.reviews.page + 1, product.reviews.limit, url)

        product.reviews = {
          ...product.reviews,
          elements: [
            ...product.reviews.elements,
            ...elements
          ],
          total,
          page,
          isFetching: false
        }
      }
    },
    async getMoreFAQ (id: string, url: string) {
      const product = this.products[id]
      if (product && !product.faq.isFetching) {
        product.faq.isFetching = true
        const { elements, total, page } = await getFaqPerPage(id, product.faq.page + 1, product.faq.limit, url)

        product.faq = {
          ...product.faq,
          elements: [
            ...product.faq.elements,
            ...elements
          ],
          total,
          page,
          isFetching: false
        }
      }
    }
  },
  getters: {
    product: ({ products }) => (id: string) => products[id]
  }
})
